import { useState, useEffect, useContext } from "react";
import axios from "axios";
import { AuthContext } from "../../context/AuthContext";
import { FaChartBar, FaCalendarDay, FaCalendarWeek, FaCalendarAlt, FaCalendar, FaClipboardList } from "react-icons/fa";
import toast from "react-hot-toast";
import { API_BASE } from "../../config";
import "../admin/Admin.css";

const STATUS_LABELS = {
  pending: "Pending",
  confirmed: "Confirmed",
  preparing: "Preparing",
  on_the_way: "On the Way",
  delivered: "Delivered",
  cancelled: "Cancelled",
};

const STATUS_COLORS = {
  pending: "#f39c12",
  confirmed: "#3498db",
  preparing: "#9b59b6",
  on_the_way: "#e67e22",
  delivered: "#27ae60",
  cancelled: "#e74c3c",
};

const PERIODS = [
  { key: "daily", label: "Daily", icon: <FaCalendarDay /> },
  { key: "weekly", label: "Weekly", icon: <FaCalendarWeek /> },
  { key: "monthly", label: "Monthly", icon: <FaCalendarAlt /> },
  { key: "yearly", label: "Yearly", icon: <FaCalendar /> },
];

const MerchantOrders = () => {
  const { token } = useContext(AuthContext);
  const [analytics, setAnalytics] = useState(null);
  const [period, setPeriod] = useState("daily");

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/merchant/orders/analytics`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAnalytics(res.data);
    } catch (error) {
      toast.error("Failed to load order analytics");
    }
  };

  if (!analytics) return <div className="admin-loading">Loading...</div>;

  const rows = analytics[period];
  const maxOrders = Math.max(1, ...rows.map((r) => r.orders));
  const maxStatus = Math.max(1, ...analytics.byStatus.map((s) => s.count));

  return (
    <div className="admin-page">
      <h1 className="admin-page-title">Orders & Analytics</h1>

      <div className="stats-grid">
        <div className="stat-card stat-blue">
          <FaCalendarDay className="stat-icon" />
          <div>
            <h3>{analytics.summary.today.orders}</h3>
            <p>Orders Today (৳{analytics.summary.today.revenue.toFixed(2)})</p>
          </div>
        </div>
        <div className="stat-card stat-green">
          <FaCalendarWeek className="stat-icon" />
          <div>
            <h3>{analytics.summary.thisWeek.orders}</h3>
            <p>This Week (৳{analytics.summary.thisWeek.revenue.toFixed(2)})</p>
          </div>
        </div>
        <div className="stat-card stat-orange">
          <FaCalendarAlt className="stat-icon" />
          <div>
            <h3>{analytics.summary.thisMonth.orders}</h3>
            <p>This Month (৳{analytics.summary.thisMonth.revenue.toFixed(2)})</p>
          </div>
        </div>
        <div className="stat-card stat-purple">
          <FaCalendar className="stat-icon" />
          <div>
            <h3>{analytics.summary.thisYear.orders}</h3>
            <p>This Year (৳{analytics.summary.thisYear.revenue.toFixed(2)})</p>
          </div>
        </div>
        <div className="stat-card stat-red">
          <FaClipboardList className="stat-icon" />
          <div>
            <h3>{analytics.summary.allTime.orders}</h3>
            <p>All-Time Orders (৳{analytics.summary.allTime.revenue.toFixed(2)})</p>
          </div>
        </div>
      </div>

      <div className="admin-form-card">
        <h3><FaChartBar /> Order Volume</h3>
        <div className="admin-filter-bar">
          {PERIODS.map((p) => (
            <button
              key={p.key}
              className={`filter-btn ${period === p.key ? "active" : ""}`}
              onClick={() => setPeriod(p.key)}
            >
              {p.icon} {p.label}
            </button>
          ))}
        </div>
        {rows.every((r) => r.orders === 0) ? (
          <p className="analytics-empty">No orders in this range yet.</p>
        ) : (
          rows.map((r) => (
            <div className="bar-meter-row" key={r.label}>
              <span className="bar-meter-label">{r.label}</span>
              <div className="bar-meter-track">
                <div
                  className="bar-meter-fill"
                  style={{ width: `${(r.orders / maxOrders) * 100}%`, background: "#3498db" }}
                />
              </div>
              <span className="bar-meter-value">{r.orders} order{r.orders === 1 ? "" : "s"}</span>
            </div>
          ))
        )}
      </div>

      <div className="admin-form-card">
        <h3><FaChartBar /> Orders by Status</h3>
        {analytics.summary.allTime.orders === 0 ? (
          <p className="analytics-empty">No orders yet — this will break down by status once you receive some.</p>
        ) : (
          analytics.byStatus.map((s) => (
            <div className="bar-meter-row" key={s.status}>
              <span className="bar-meter-label">{STATUS_LABELS[s.status]}</span>
              <div className="bar-meter-track">
                <div
                  className="bar-meter-fill"
                  style={{ width: `${(s.count / maxStatus) * 100}%`, background: STATUS_COLORS[s.status] }}
                />
              </div>
              <span className="bar-meter-value">{s.count}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default MerchantOrders;
